/**
 * Roof Geometry CAD-lite editor.
 *
 * Geometry is edited in the browser, while validity, area and perimeter stay
 * backend-authoritative. Site Layout remains the owner of whole-roof project
 * placement; this editor owns the local roof outline and its openings.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { geometryService } from '../../api/client';
import type { ValidationMessage } from '../../gen/ejot/roofing/v1/types';
import { MessageList } from '../../shared/components';
import { useHistory } from '../../shared/history';
import { usePinchGesture } from '../../shared/usePinchGesture';
import {
  DEFAULT_VIEW,
  fitBounds,
  formatMillimetres,
  pinchView,
  snap,
  toScreen,
  toWorld,
  zoomAt,
  type SnapSettings,
  type ViewTransform,
  type WorldPoint,
} from './canvas';
import { GeometryToolPalette } from './GeometryToolPalette';
import { formatDraftMetres, isDynamicNumericKey, parseMetres, pointAtLength, pointAtOffsets } from './dynamicPolygonInput';
import { CoordinateIndicator } from '../../shared/CoordinateIndicator';
import {
  hitTest,
  projectPointToSegment,
  selectionBounds,
  selectionForObjectId,
  type GeometryDraft,
  type GeometrySelection,
  type GeometryTool,
} from './interaction';
import {
  addPolygonOpening,
  addRectangularOpening,
  allPoints,
  bounds,
  deleteOpening,
  deleteVertex,
  findPoint,
  insertVertex,
  moveVertex,
  pointsFor,
  setRoofPolygon,
  toContract,
  translateOpening,
  type GeometryModel,
  type GeometryOwner,
} from './model';
import { render, type LayerVisibility } from './render';
import './geometry-editor.css';

interface RoofPlanEditorProps {
  layers: LayerVisibility;
  geometry: GeometryModel;
  onGeometryChange: (model: GeometryModel) => void;
}

type DragState =
  | {
      kind: 'vertex';
      owner: GeometryOwner;
      pointId: string;
      baseline: GeometryModel;
      origin: WorldPoint;
      latest: WorldPoint;
    }
  | {
      kind: 'opening';
      openingId: string;
      baseline: GeometryModel;
      start: WorldPoint;
      dx: number;
      dy: number;
    };

interface PanningState {
  x: number;
  y: number;
  view: ViewTransform;
}

export function RoofPlanEditor({ layers, geometry, onGeometryChange }: RoofPlanEditorProps) {
  const history = useHistory<GeometryModel>(geometry);
  const model = history.model;
  const resetHistory = history.reset;
  const displayModelRef = useRef(model);

  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const publishedRevisionRef = useRef(model.revision);
  const dragRef = useRef<DragState>();
  const panningRef = useRef<PanningState>();

  const [size, setSize] = useState({ width: 800, height: 600 });
  const [view, setView] = useState<ViewTransform>(DEFAULT_VIEW);
  const [tool, setTool] = useState<GeometryTool>(model.closed ? 'select' : 'draw-roof-polygon');
  const [selection, setSelection] = useState<GeometrySelection>();
  const [draft, setDraft] = useState<GeometryDraft>();
  const [previewModel, setPreviewModel] = useState<GeometryModel>();
  const [cursor, setCursor] = useState<WorldPoint>();
  const [coordinatesOpen, setCoordinatesOpen] = useState(true);
  const [spacePan, setSpacePan] = useState(false);
  const [dynamicEntry, setDynamicEntry] = useState({ mode: 'length' as 'length' | 'xy', first: '', second: '' });
  const [snapSettings, setSnapSettings] = useState<SnapSettings>({
    gridSizeMM: 1000,
    toGrid: true,
    toPoints: true,
    orthogonal: false,
  });

  const displayModel = previewModel ?? model;
  displayModelRef.current = displayModel;

  const validation = useMutation({
    mutationFn: (current: GeometryModel) => geometryService.validateRoofGeometry({
      geometry: toContract(current),
      request_token: String(current.revision),
    }),
  });

  const messages: ValidationMessage[] = useMemo(
    () => validation.data?.messages ?? [],
    [validation.data],
  );
  const problemObjectIds = useMemo(
    () => messages.flatMap((message) => message.affected_object_id ? [message.affected_object_id] : []),
    [messages],
  );
  const problemMessages = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const message of messages) {
      const id = message.affected_object_id;
      if (!id) continue;
      const text = message.title ?? message.explanation ?? message.code ?? 'Geometry issue';
      map.set(id, [...(map.get(id) ?? []), text]);
    }
    return map;
  }, [messages]);

  const commit = useCallback((label: string, produce: (current: GeometryModel) => GeometryModel) => {
    history.run(label, (current) => {
      const next = produce(current);
      if (next === current) return current;
      return { ...next, revision: current.revision + 1 };
    });
  }, [history]);

  useEffect(() => {
    if (
      geometry.revision !== publishedRevisionRef.current
      && geometry.revision !== model.revision
    ) {
      resetHistory(geometry);
      publishedRevisionRef.current = geometry.revision;
      setSelection(undefined);
      setDraft(undefined);
      setPreviewModel(undefined);
      dragRef.current = undefined;
      setTool(geometry.closed ? 'select' : 'draw-roof-polygon');
    }
  }, [geometry, model.revision, resetHistory]);

  useEffect(() => {
    publishedRevisionRef.current = model.revision;
    onGeometryChange(model);
    if (model.closed && model.points.length >= 3) validation.mutate(model);
    // mutation identity is stable; including it would retrigger on mutation state updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, onGeometryChange]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect) setSize({ width: Math.max(1, Math.floor(rect.width)), height: Math.max(1, Math.floor(rect.height)) });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    render(canvas, displayModel, {
      view,
      width: size.width,
      height: size.height,
      layers,
      selection,
      draft,
      hoverPoint: cursor,
      problemObjectIds,
      gridSizeMM: snapSettings.gridSizeMM,
    });
  }, [displayModel, view, size, layers, selection, draft, cursor, problemObjectIds, snapSettings.gridSizeMM]);

  const screenFromEvent = (event: React.PointerEvent | React.WheelEvent) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    return {
      x: event.clientX - (rect?.left ?? 0),
      y: event.clientY - (rect?.top ?? 0),
    };
  };

  const resolve = useCallback((
    screen: { x: number; y: number },
    sourceModel = displayModelRef.current,
    excludePointId?: string,
    anchor?: WorldPoint,
  ) => {
    const world = toWorld(view, screen, size.height);
    const candidates = allPoints(sourceModel)
      .filter((candidate) => candidate.id !== excludePointId)
      .map((candidate) => ({ id: candidate.id, x: candidate.x, y: candidate.y }));

    if (draft?.kind === 'polygon') {
      draft.points.forEach((candidate, index) => {
        candidates.push({ id: `draft-${index}`, x: candidate.x, y: candidate.y });
      });
    }

    return snap(world, view, snapSettings, candidates, anchor);
  }, [draft, size.height, snapSettings, view]);

  const pinch = usePinchGesture({
    onGestureStart: () => {
      dragRef.current = undefined;
      panningRef.current = undefined;
      setPreviewModel(undefined);
    },
    onPinch: (update) => {
      setView((current) => pinchView(
        current,
        update.centre,
        update.scaleFactor,
        update.panX,
        update.panY,
        size.height,
      ));
    },
  });

  const rectangleCoordinates = (rectangle: Extract<GeometryDraft, { kind: 'rectangle' }>) => {
    const minX = Math.min(rectangle.start.x, rectangle.end.x);
    const minY = Math.min(rectangle.start.y, rectangle.end.y);
    const maxX = Math.max(rectangle.start.x, rectangle.end.x);
    const maxY = Math.max(rectangle.start.y, rectangle.end.y);
    return [
      { x: minX, y: minY },
      { x: maxX, y: minY },
      { x: maxX, y: maxY },
      { x: minX, y: maxY },
    ];
  };

  const finishPolygon = useCallback(() => {
    if (draft?.kind !== 'polygon' || draft.points.length < 3) return;
    const points = draft.points;
    if (draft.owner === 'roof') {
      commit('draw roof polygon', (current) => setRoofPolygon(current, points));
    } else {
      commit('draw polygon opening', (current) => addPolygonOpening(current, points));
    }
    setDraft(undefined);
    setSelection(undefined);
    setTool('select');
  }, [commit, draft]);

  const applySelectionDelete = useCallback(() => {
    if (!selection) return;
    if (selection.kind === 'opening') {
      commit('delete opening', (current) => deleteOpening(current, selection.openingId));
      setSelection(undefined);
      return;
    }
    if (selection.kind === 'vertex') {
      commit('delete vertex', (current) => deleteVertex(current, selection.owner, selection.pointId));
      setSelection(undefined);
    }
  }, [commit, selection]);

  const insertSelectedVertex = useCallback(() => {
    if (selection?.kind !== 'segment') return;
    commit('insert vertex', (current) => insertVertex(current, selection.owner, selection.segmentIndex));
    setSelection(undefined);
  }, [commit, selection]);

  const startTool = useCallback((nextTool: GeometryTool) => {
    setDynamicEntry({ mode: 'length', first: '', second: '' });
    setTool(nextTool);
    setDraft(undefined);
    setPreviewModel(undefined);
    dragRef.current = undefined;
    if (nextTool !== 'move-opening') setSelection(undefined);
  }, []);

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    event.currentTarget.focus();
    if (pinch.down(event)) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const screen = screenFromEvent(event);
    const rawWorld = toWorld(view, screen, size.height);

    if (event.button === 1 || spacePan || tool === 'pan') {
      panningRef.current = { x: event.clientX, y: event.clientY, view };
      return;
    }

    if (tool === 'draw-roof-rectangle' || tool === 'draw-opening-rectangle') {
      if (tool === 'draw-opening-rectangle' && !model.closed) return;
      const candidate = resolve(screen, model);
      setDraft({
        kind: 'rectangle',
        owner: tool === 'draw-roof-rectangle' ? 'roof' : 'opening',
        start: { x: candidate.x, y: candidate.y },
        end: { x: candidate.x, y: candidate.y },
      });
      return;
    }

    if (tool === 'draw-roof-polygon' || tool === 'draw-opening-polygon') {
      if (tool === 'draw-opening-polygon' && !model.closed) return;
      const owner = tool === 'draw-roof-polygon' ? 'roof' : 'opening';
      const anchor = draft?.kind === 'polygon' ? draft.points[draft.points.length - 1] : undefined;
      const candidate = resolve(screen, model, undefined, anchor);

      if (draft?.kind === 'polygon' && draft.owner === owner) {
        setDynamicEntry({ mode: 'length', first: '', second: '' });
        if (candidate.pointId === 'draft-0' && draft.points.length >= 3) {
          finishPolygon();
          return;
        }
        setDraft({ ...draft, points: [...draft.points, { x: candidate.x, y: candidate.y }], cursor: undefined });
      } else {
        setDynamicEntry({ mode: 'length', first: '', second: '' });
        setDraft({ kind: 'polygon', owner, points: [{ x: candidate.x, y: candidate.y }] });
      }
      return;
    }

    if (tool === 'insert-vertex') {
      const hit = hitTest(model, rawWorld, 12 / view.scale);
      if (hit?.kind !== 'segment') {
        setSelection(hit);
        return;
      }
      const ring = pointsFor(model, hit.owner);
      const start = ring[hit.segmentIndex];
      const end = ring[(hit.segmentIndex + 1) % ring.length];
      if (!start || !end) return;
      const projected = projectPointToSegment(rawWorld, start, end);
      commit('insert vertex', (current) => insertVertex(current, hit.owner, hit.segmentIndex, projected));
      setSelection(undefined);
      setTool('select');
      return;
    }

    let hit = hitTest(model, rawWorld, 12 / view.scale);
    if (tool === 'move-opening') {
      if (hit?.kind === 'vertex' && hit.owner.kind === 'opening') hit = { kind: 'opening', openingId: hit.owner.openingId };
      if (hit?.kind === 'segment' && hit.owner.kind === 'opening') hit = { kind: 'opening', openingId: hit.owner.openingId };
      if (hit?.kind !== 'opening') {
        setSelection(hit);
        return;
      }
    }

    setSelection(hit);
    if (!hit) return;

    if (hit.kind === 'vertex' && tool === 'select') {
      const selectedPoint = findPoint(model, hit.owner, hit.pointId);
      if (!selectedPoint) return;
      dragRef.current = {
        kind: 'vertex',
        owner: hit.owner,
        pointId: hit.pointId,
        baseline: model,
        origin: { x: selectedPoint.x, y: selectedPoint.y },
        latest: { x: selectedPoint.x, y: selectedPoint.y },
      };
      return;
    }

    if (hit.kind === 'opening' && (tool === 'select' || tool === 'move-opening')) {
      dragRef.current = { kind: 'opening', openingId: hit.openingId, baseline: model, start: rawWorld, dx: 0, dy: 0 };
    }
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (pinch.move(event)) return;
    const screen = screenFromEvent(event);

    if (panningRef.current) {
      const start = panningRef.current;
      setView({
        ...start.view,
        offsetX: start.view.offsetX - (event.clientX - start.x) / start.view.scale,
        offsetY: start.view.offsetY + (event.clientY - start.y) / start.view.scale,
      });
      return;
    }

    const rawWorld = toWorld(view, screen, size.height);
    let anchor: WorldPoint | undefined;
    if (draft?.kind === 'polygon') anchor = draft.points[draft.points.length - 1];
    if (draft?.kind === 'rectangle') anchor = draft.start;
    if (dragRef.current?.kind === 'vertex') anchor = dragRef.current.origin;
    if (dragRef.current?.kind === 'opening') anchor = dragRef.current.start;

    const candidate = resolve(
      screen,
      dragRef.current?.baseline ?? model,
      dragRef.current?.kind === 'vertex' ? dragRef.current.pointId : undefined,
      anchor,
    );
    setCursor({ x: candidate.x, y: candidate.y });

    if (draft?.kind === 'rectangle') {
      setDraft({ ...draft, end: { x: candidate.x, y: candidate.y } });
      return;
    }
    if (draft?.kind === 'polygon') {
      setDraft({ ...draft, cursor: { x: candidate.x, y: candidate.y } });
      return;
    }

    const dragging = dragRef.current;
    if (dragging?.kind === 'vertex') {
      dragging.latest = { x: candidate.x, y: candidate.y };
      setPreviewModel(moveVertex(dragging.baseline, dragging.owner, dragging.pointId, candidate.x, candidate.y));
      return;
    }

    if (dragging?.kind === 'opening') {
      dragging.dx = candidate.x - dragging.start.x;
      dragging.dy = candidate.y - dragging.start.y;
      setPreviewModel(translateOpening(dragging.baseline, dragging.openingId, dragging.dx, dragging.dy));
      return;
    }

    if (!Number.isFinite(candidate.x) || !Number.isFinite(candidate.y)) setCursor(rawWorld);
  };

  const onPointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    pinch.up(event);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);

    if (panningRef.current) {
      panningRef.current = undefined;
      return;
    }

    if (draft?.kind === 'rectangle') {
      const rectangle = draft;
      const coordinates = rectangleCoordinates(rectangle);
      const width = Math.abs(rectangle.end.x - rectangle.start.x);
      const height = Math.abs(rectangle.end.y - rectangle.start.y);
      setDraft(undefined);
      if (width > 0 && height > 0) {
        if (rectangle.owner === 'roof') {
          commit('draw roof rectangle', (current) => setRoofPolygon(current, coordinates));
        } else {
          const minX = coordinates[0]?.x ?? 0;
          const minY = coordinates[0]?.y ?? 0;
          commit('draw rectangular opening', (current) => addRectangularOpening(current, minX, minY, width, height));
        }
        setTool('select');
        setSelection(undefined);
      }
      return;
    }

    const dragging = dragRef.current;
    dragRef.current = undefined;
    setPreviewModel(undefined);

    if (dragging?.kind === 'vertex') {
      const { latest } = dragging;
      if (latest.x !== dragging.origin.x || latest.y !== dragging.origin.y) {
        commit('move vertex', (current) => moveVertex(current, dragging.owner, dragging.pointId, latest.x, latest.y));
      }
      return;
    }

    if (dragging?.kind === 'opening' && (dragging.dx !== 0 || dragging.dy !== 0)) {
      commit('move opening', (current) => translateOpening(current, dragging.openingId, dragging.dx, dragging.dy));
    }
  };

  const onWheel = (event: React.WheelEvent<HTMLCanvasElement>) => {
    event.preventDefault();
    const screen = screenFromEvent(event);
    setView((current) => zoomAt(current, screen, event.deltaY > 0 ? 0.9 : 1.1, size.height));
  };

  const fit = useCallback(() => {
    const target = bounds(model);
    if (target.min.x === target.max.x && target.min.y === target.max.y) setView(DEFAULT_VIEW);
    else setView(fitBounds(target.min, target.max, size.width, size.height));
  }, [model, size]);

  const fitSelection = useCallback(() => {
    const selected = selectionBounds(model, selection);
    if (!selected) {
      fit();
      return;
    }
    setView(fitBounds(selected.min, selected.max, size.width, size.height, 72));
  }, [fit, model, selection, size]);

  const focusFirstProblem = useCallback(() => {
    const id = problemObjectIds[0];
    if (!id) return;
    const selected = selectionForObjectId(model, id);
    if (!selected) {
      fit();
      return;
    }
    setSelection(selected);
    const target = selectionBounds(model, selected);
    if (target) setView(fitBounds(target.min, target.max, size.width, size.height, 84));
  }, [fit, model, problemObjectIds, size]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (isTypingTarget(event.target)) return;

    if (draft?.kind === 'polygon' && draft.points.length > 0) {
      if (isDynamicNumericKey(event.key)) {
        event.preventDefault();
        setDynamicEntry((current) => current.mode === 'xy'
          ? { ...current, second: current.second + event.key }
          : { ...current, first: current.first + event.key });
        return;
      }
      if (event.key === 'Backspace' && (dynamicEntry.first || dynamicEntry.second)) {
        event.preventDefault();
        setDynamicEntry((current) => current.mode === 'xy' && current.second
          ? { ...current, second: current.second.slice(0, -1) }
          : { ...current, first: current.first.slice(0, -1) });
        return;
      }
      if (event.key === 'Tab' && dynamicEntry.first) {
        event.preventDefault();
        setDynamicEntry((current) => ({ ...current, mode: 'xy' }));
        return;
      }
      if (event.key === 'Enter' && dynamicEntry.first) {
        event.preventDefault();
        const anchor = draft.points[draft.points.length - 1];
        if (!anchor) return;
        let next: WorldPoint | undefined;
        if (dynamicEntry.mode === 'xy') {
          const x = parseMetres(dynamicEntry.first);
          const y = parseMetres(dynamicEntry.second);
          if (x !== undefined && y !== undefined) next = pointAtOffsets(anchor, x, y);
        } else {
          const length = parseMetres(dynamicEntry.first);
          const direction = draft.cursor ?? cursor;
          if (length !== undefined && direction) next = pointAtLength(anchor, direction, length);
        }
        if (next) {
          setDraft({ ...draft, points: [...draft.points, next], cursor: next });
          setCursor(next);
          setDynamicEntry({ mode: 'length', first: '', second: '' });
        }
        return;
      }
    }

    if (event.code === 'Space') { event.preventDefault(); setSpacePan(true); return; }
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
      event.preventDefault();
      if (event.shiftKey) history.redo(); else history.undo();
      return;
    }
    if ((event.ctrlKey || event.metaKey) && event.key === '0') { event.preventDefault(); fit(); return; }
    if (event.key === 'Delete' || event.key === 'Backspace') { event.preventDefault(); applySelectionDelete(); return; }
    if (event.key === 'Enter' && draft?.kind === 'polygon') { event.preventDefault(); finishPolygon(); setDynamicEntry({ mode: 'length', first: '', second: '' }); return; }
    if (event.key === 'Escape') {
      event.preventDefault();
      if (draft) { setDraft(undefined); setDynamicEntry({ mode: 'length', first: '', second: '' }); return; }
      setSelection(undefined);
      setTool('select');
      return;
    }

    const key = event.key.toLowerCase();
    if (key === 'v') startTool('select');
    if (key === 'r' && event.shiftKey) startTool('draw-opening-rectangle'); else if (key === 'r') startTool('draw-roof-rectangle');
    if (key === 'p' && event.shiftKey) startTool('draw-opening-polygon'); else if (key === 'p') startTool('draw-roof-polygon');
    if (key === 'i') startTool('insert-vertex');
  };

  const onKeyUp = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.code === 'Space') setSpacePan(false);
  };

  const updateCoordinate = useCallback((owner: GeometryOwner, pointId: string, x: number, y: number) => {
    commit('set vertex coordinates', (current) => moveVertex(current, owner, pointId, x, y));
  }, [commit]);

  const derived = validation.data?.geometry;

  return (
    <div className="roof-geometry-editor plan-editor" onKeyDown={onKeyDown} onKeyUp={onKeyUp} tabIndex={0}>
      <GeometryToolPalette
        tool={tool}
        onTool={startTool}
        snapSettings={snapSettings}
        onSnapChange={setSnapSettings}
        selection={selection}
        model={model}
        history={history}
        onInsertVertex={insertSelectedVertex}
        onDeleteSelected={applySelectionDelete}
        onFit={fit}
        onFitSelection={fitSelection}
      />

      <div className="geometry-editor-workspace">
        <CoordinateInspector
          model={displayModel}
          open={coordinatesOpen}
          onOpenChange={setCoordinatesOpen}
          selection={selection}
          onSelection={setSelection}
          problemMessages={problemMessages}
          onCoordinateChange={updateCoordinate}
        />

        <div className="geometry-canvas-shell">
          <div className="plan-canvas-host" ref={containerRef}>
            <canvas
              ref={canvasRef}
              className="plan-canvas"
              tabIndex={0}
              style={{ width: size.width, height: size.height, touchAction: 'none' }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              onPointerLeave={() => setCursor(undefined)}
              onWheel={onWheel}
            />
            {draft?.kind === 'polygon' && cursor && (() => {
              const screen = toScreen(view, cursor, size.height);
              const anchor = draft.points[draft.points.length - 1];
              if (!anchor) return null;
              const dx = cursor.x - anchor.x;
              const dy = cursor.y - anchor.y;
              const xText = dynamicEntry.first
                ? `${dynamicEntry.first} m`
                : formatDraftMetres(dx);
              const yText = dynamicEntry.mode === 'xy' && dynamicEntry.second
                ? `${dynamicEntry.second} m`
                : formatDraftMetres(dy);
              return (
                <div className="geometry-dynamic-input" style={{ left: screen.x + 14, top: screen.y + 14 }} aria-live="polite">
                  <span className={dynamicEntry.first && dynamicEntry.mode === 'length' ? 'is-active' : undefined}>X {xText}</span>
                  <span className={dynamicEntry.mode === 'xy' ? 'is-active' : undefined}>Y {yText}</span>
                  <small>{dynamicEntry.mode === 'xy' ? 'Enter: place X/Y offset' : 'Type + Enter: length · Tab: X/Y'}</small>
                </div>
              );
            })()}
            <CoordinateIndicator mode="2d" visible={layers.axes !== false} />
            <div className="geometry-canvas-hint">{toolHint(tool, draft)}</div>
          </div>

          <div className="plan-status" role="status" aria-live="polite">
            <span className="plan-status-item">{cursor ? `X ${formatMillimetres(cursor.x)}   Y ${formatMillimetres(cursor.y)}` : 'Move the cursor over the canvas'}</span>
            <span className="plan-status-item">Roof vertices {model.points.length}</span>
            <span className="plan-status-item">Openings {model.openings.length}</span>
            <span className="plan-status-item">Area {derived?.gross_area ? `${(derived.gross_area.square_metres ?? 0).toFixed(1)} m²` : '-'}</span>
            <span className="plan-status-item">Perimeter {derived?.perimeter ? formatMillimetres(derived.perimeter.millimetres ?? 0) : '-'}</span>
          </div>
        </div>
      </div>

      {messages.length > 0 && (
        <div className="plan-messages">
          {problemObjectIds.length > 0 && <button type="button" className="toolbar-button" onClick={focusFirstProblem}>Focus first geometry issue</button>}
          <MessageList messages={messages} emptyText="" />
        </div>
      )}
    </div>
  );
}

function CoordinateInspector({ model, open, onOpenChange, selection, onSelection, problemMessages, onCoordinateChange }: {
  model: GeometryModel;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selection: GeometrySelection | undefined;
  onSelection: (selection: GeometrySelection | undefined) => void;
  problemMessages: Map<string, string[]>;
  onCoordinateChange: (owner: GeometryOwner, pointId: string, x: number, y: number) => void;
}) {
  return (
    <aside className={open ? 'geometry-coordinates' : 'geometry-coordinates is-collapsed'} aria-label="Geometry coordinates">
      <div className="geometry-coordinate-header">
        <span className="geometry-coordinate-title">Coordinates</span>
        <button type="button" className="geometry-coordinate-toggle" aria-label={open ? 'Hide coordinates' : 'Show coordinates'} title={open ? 'Hide coordinates' : 'Show coordinates'} onClick={() => onOpenChange(!open)}>{open ? '‹' : '›'}</button>
      </div>
      <div className="geometry-coordinate-body">
        <div className="geometry-coordinate-columns" aria-hidden="true"><span>Point</span><span>X (m)</span><span>Y (m)</span><span /></div>
        <CoordinateGroup title="Roof Boundary" owner={{ kind: 'roof' }} points={model.points} prefix="P" selection={selection} onSelection={onSelection} problemMessages={problemMessages} onCoordinateChange={onCoordinateChange} />
        {model.openings.map((opening, openingIndex) => (
          <CoordinateGroup key={opening.id} title={opening.displayName || `Opening ${openingIndex + 1}`} owner={{ kind: 'opening', openingId: opening.id }} points={opening.points} prefix={`O${openingIndex + 1}-P`} selection={selection} onSelection={onSelection} problemMessages={problemMessages} groupProblem={problemMessages.has(opening.id)} onCoordinateChange={onCoordinateChange} />
        ))}
      </div>
    </aside>
  );
}

function CoordinateGroup({ title, owner, points, prefix, selection, onSelection, problemMessages, groupProblem = false, onCoordinateChange }: {
  title: string;
  owner: GeometryOwner;
  points: readonly { id: string; x: number; y: number }[];
  prefix: string;
  selection: GeometrySelection | undefined;
  onSelection: (selection: GeometrySelection | undefined) => void;
  problemMessages: Map<string, string[]>;
  groupProblem?: boolean;
  onCoordinateChange: (owner: GeometryOwner, pointId: string, x: number, y: number) => void;
}) {
  const hasPointProblem = points.some((point) => problemMessages.has(point.id));
  return (
    <details className="geometry-coordinate-group" open>
      <summary><span>{title}</span>{(groupProblem || hasPointProblem) && <span className="geometry-opening-problem" title="Geometry issue" />}</summary>
      {points.map((point, index) => {
        const selected = selection?.kind === 'vertex' && selection.pointId === point.id;
        const label = `${prefix}${index + 1}`;
        return <CoordinateRow key={point.id} label={label} point={point} selected={selected} problem={problemMessages.get(point.id)} onSelect={() => onSelection({ kind: 'vertex', owner, pointId: point.id })} onCommit={(x, y) => onCoordinateChange(owner, point.id, x, y)} />;
      })}
    </details>
  );
}

function CoordinateRow({ label, point, selected, problem, onSelect, onCommit }: {
  label: string;
  point: { id: string; x: number; y: number };
  selected: boolean;
  problem?: string[];
  onSelect: () => void;
  onCommit: (x: number, y: number) => void;
}) {
  const format = (value: number) => (value / 1000).toFixed(3);
  const [x, setX] = useState(format(point.x));
  const [y, setY] = useState(format(point.y));

  useEffect(() => { setX(format(point.x)); setY(format(point.y)); }, [point.id, point.x, point.y]);
  const reset = () => { setX(format(point.x)); setY(format(point.y)); };
  const commitValue = () => {
    const parsedX = Number(x.replace(',', '.'));
    const parsedY = Number(y.replace(',', '.'));
    if (!Number.isFinite(parsedX) || !Number.isFinite(parsedY)) { reset(); return; }
    onCommit(parsedX * 1000, parsedY * 1000);
  };
  const keyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') { event.preventDefault(); commitValue(); event.currentTarget.blur(); }
    if (event.key === 'Escape') { event.preventDefault(); reset(); event.currentTarget.blur(); }
  };

  return (
    <div className={selected ? 'geometry-coordinate-row is-selected' : 'geometry-coordinate-row'} onClick={onSelect}>
      <span className="geometry-coordinate-label" title={point.id}>{label}</span>
      <input className="geometry-coordinate-input" aria-label={`${label} X coordinate in metres`} inputMode="decimal" value={x} onFocus={onSelect} onClick={(event) => event.stopPropagation()} onChange={(event) => setX(event.target.value)} onBlur={commitValue} onKeyDown={keyDown} />
      <input className="geometry-coordinate-input" aria-label={`${label} Y coordinate in metres`} inputMode="decimal" value={y} onFocus={onSelect} onClick={(event) => event.stopPropagation()} onChange={(event) => setY(event.target.value)} onBlur={commitValue} onKeyDown={keyDown} />
      {problem?.length ? <span className="geometry-coordinate-problem" title={problem.join('\n')} /> : <span />}
    </div>
  );
}

function toolHint(tool: GeometryTool, draft: GeometryDraft | undefined): string {
  if (tool === 'select') return 'Select: drag vertices or an opening. Roof body movement stays in Site Layout.';
  if (tool === 'pan') return 'Pan: drag the canvas. You can also hold Space from any tool.';
  if (tool === 'draw-roof-rectangle') return 'Rectangle roof: drag from one corner to the opposite corner.';
  if (tool === 'draw-roof-polygon') return draft ? 'Polygon roof: click more vertices; click the first point or press Enter to close; Esc cancels.' : 'Polygon roof: click the first vertex.';
  if (tool === 'draw-opening-rectangle') return 'Rectangular opening: drag inside the roof. Backend validation reports invalid placement.';
  if (tool === 'draw-opening-polygon') return draft ? 'Polygon opening: click more vertices; click the first point or press Enter to close; Esc cancels.' : 'Polygon opening: click the first vertex.';
  if (tool === 'insert-vertex') return 'Add Vertex: click an existing roof or opening edge.';
  return 'Move Opening: drag an opening as one rigid shape.';
}

function isTypingTarget(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  if (!element) return false;
  const tag = element.tagName?.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || element.isContentEditable;
}
